import React, { FunctionComponent, useEffect, useState, useRef, useCallback } from 'react';

type Props = {
  name: string;
  value?: string;
  type?: string;
  className?: string;
  placeholder?: string;
  pattern?: string;
  maxLength?: number;
  isFocus?: boolean;
  onChanged: (name: string, value: string) => void;
  handleSend?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  onBeforeInput?: (e: React.FormEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  enterKeyHint?: React.HTMLAttributes<HTMLInputElement>['enterKeyHint'];
};

export const Presentation: FunctionComponent<Props> = ({
  name,
  value,
  type,
  className,
  placeholder,
  pattern,
  maxLength,
  isFocus,
  onChanged,
  handleSend,
  onKeyDown,
  onPaste,
  onBeforeInput,
  onBlur,
  enterKeyHint,
}) => {
  const [internalValue, setInternalValue] = useState<string>(value ?? '');
  const [isComposing, setIsComposing] = useState(false);
  const focusRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isFocus) {
      focusRef.current?.focus();
    }
  }, [isFocus]);

  useEffect(() => {
    if (!isComposing) {
      setInternalValue(value ?? '');
    }
  }, [value, isComposing]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInternalValue(newValue);
    if (!isComposing) {
      onChanged(name, newValue);
    }
  };

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = (event: React.CompositionEvent<HTMLInputElement>) => {
    const finalValue = event.currentTarget.value;
    setIsComposing(false);
    setInternalValue(finalValue);
    onChanged(name, finalValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      (name === 'targetSkill' || name === 'targetInterest' || name === 'targetBrand') &&
      e.key === 'Enter' &&
      !isComposing
    ) {
      handleSend?.(e);
    }
  };

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setInternalValue(e.currentTarget.value);
      onBlur?.(e);
    },
    [onBlur]
  );

  return (
    <input
      ref={focusRef}
      type={type}
      className={className}
      placeholder={placeholder}
      value={internalValue}
      maxLength={maxLength}
      onChange={handleChange}
      onKeyDown={(e) => {
        handleKeyDown(e);
        onKeyDown?.(e);
      }}
      onBeforeInput={onBeforeInput}
      onPaste={onPaste}
      onBlur={handleBlur}
      pattern={pattern}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      enterKeyHint={enterKeyHint}
    />
  );
};
